import React, { useContext, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthContext } from './context/AuthContext';
import { usePermission } from './context/PermissionContext';
import { initializeRoles } from './utils/roleUtils';

// Sayfa bileşenleri
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import RefundListPage from './pages/RefundListPage';
import RefundCreatePage from './pages/RefundCreatePage';
import RefundDetailPage from './pages/RefundDetailPage';
import RefundEditPage from './pages/RefundEditPage';
import NotFoundPage from './pages/NotFoundPage';
import UsersPage from './pages/UsersPage';
import AdminPanelPage from './pages/AdminPanelPage';
import LogsPage from './pages/LogsPage';
import ProfilePage from './pages/ProfilePage';
import ReportsPage from './pages/ReportsPage';

// Geçici Sayfalar
import { SettingsPage } from './pages/PlaceholderPages';

// Layout bileşeni
import Layout from './components/layout/Layout';

// Yetkilendirme için özel Route bileşeni
const PrivateRoute = ({ children, allowedRoles = [], requiredPermissions = [] }) => {
  const { isAuthenticated, user, loading } = useContext(AuthContext);
  const { hasPermission } = usePermission();

  // Yükleme durumunda bir loading göster
  if (loading) {
    return <div className="flex justify-center items-center h-screen">Yükleniyor...</div>;
  }

  // Kullanıcı giriş yapmamışsa login sayfasına yönlendir
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Belirli roller için erişim kontrolü
  if (allowedRoles.length > 0 && user) {
    // Admin her sayfaya erişebilir
    if (user.role === 'admin') {
      return children;
    }
    
    // Diğer roller için kontrol
    if (!allowedRoles.includes(user.role)) {
      return <Navigate to="/dashboard" />;
    }
  }
  
  // İzin kontrolü (hem rol hem de izin şartını sağlamalı)
  if (requiredPermissions.length > 0) {
    // En az bir izne sahip olması yeterli (OR logic)
    const hasAnyPermission = requiredPermissions.some(perm => hasPermission(perm));
    
    if (!hasAnyPermission) {
      return <Navigate to="/dashboard" />;
    }
  }

  return children;
};

function App() {
  // Uygulama yüklendiğinde rolleri yükle
  useEffect(() => {
    // Rolleri başlatma fonksiyonunu çağır
    initializeRoles().then(roles => {
      console.log('Roller başarıyla yüklendi', roles?.length || 0);
    }).catch(error => {
      console.error('Roller yüklenirken hata oluştu:', error);
    });
  }, []);
  
  return (
    <Routes>
      {/* Giriş sayfası - public */}
      <Route path="/login" element={<LoginPage />} />

      {/* Korumalı rotalar */}
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        
        {/* İade listesi - Tüm giriş yapmış kullanıcılar için erişilebilir */}
        <Route 
          path="refunds" 
          element={
            <PrivateRoute>
              <RefundListPage />
            </PrivateRoute>
          } 
        />
        
        {/* Yeni iade oluşturma - sadece 'create_refund' yetkisi olanlar */}
        <Route 
          path="refunds/create" 
          element={
            <PrivateRoute requiredPermissions={['create_refund']}>
              <RefundCreatePage />
            </PrivateRoute>
          } 
        />
        
        {/* İade düzenleme - sadece 'create_refund' yetkisi olanlar */}
        <Route 
          path="refunds/edit/:id" 
          element={
            <PrivateRoute requiredPermissions={['create_refund']}>
              <RefundEditPage />
            </PrivateRoute>
          } 
        />
        
        {/* İade detayı - Herhangi bir giriş yapmış kullanıcı erişebilir */}
        <Route 
          path="refunds/:id" 
          element={
            <PrivateRoute>
              <RefundDetailPage />
            </PrivateRoute>
          } 
        />
        
        {/* Kullanıcı yönetimi - sadece CASI veya Admin */}
        <Route 
          path="users" 
          element={
            <PrivateRoute allowedRoles={['casi', 'admin']}>
              <UsersPage />
            </PrivateRoute>
          } 
        />
        
        {/* Admin paneli - yönetim izinlerinden biri yeterli */}
        <Route 
          path="admin" 
          element={
            <PrivateRoute requiredPermissions={['view_admin_panel', 'manage_users', 'manage_roles', 'manage_statuses', 'manage_payment_methods', 'manage_reasons']}>
              <AdminPanelPage />
            </PrivateRoute>
          } 
        />
        
        <Route path="settings" element={<SettingsPage />} />
        <Route 
          path="reports" 
          element={
            <PrivateRoute requiredPermissions={['view_reports']}>
              <ReportsPage />
            </PrivateRoute>
          } 
        />
        <Route 
          path="logs" 
          element={
            <PrivateRoute requiredPermissions={['view_logs']}>
              <LogsPage />
            </PrivateRoute>
          } 
        />
        
        {/* Profil sayfası - Tüm giriş yapmış kullanıcılar */}
        <Route 
          path="profile" 
          element={
            <PrivateRoute>
              <ProfilePage />
            </PrivateRoute>
          } 
        />
      </Route>

      {/* 404 sayfası */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default App;